export const colors = {
  primary: {
    50: '#b8ebe6',
    100: '#73d7ce',
    200: '#2ec4b6',
    300: '#20897f',
    400: '#0091ae',
    500: '#008992', // Main brand teal
    600: '#007674',
    700: '#00655d',
    800: '#00544a',
    900: '#004337',
  },

  secondary: {
    50: '#fff9e6',
    100: '#ffedb3',
    200: '#ffe180',
    300: '#ffd54d',
    400: '#fdc449', // Main gold
    500: '#febd11',
    600: '#cc9a0e',
    700: '#99770b',
    800: '#665408',
    900: '#333205',
  },

  accent: {
    navy: '#201F50',
    navyDark: '#011627',
    teal: '#2ec4b6',
    tealLight: '#73d7ce',
    tealVeryLight: '#b8ebe6',
  },

  neutral: {
    50: '#fdfffc', // Off-white
    100: '#eaf0f6', // Very light blue-gray
    200: '#e8e8e8', // Light gray
    300: '#cbd6e2', // Medium gray
    400: '#8b8589', // Gray
    500: '#666666', // Medium dark gray
    600: '#3f3f3f', // Dark gray
    700: '#313131', // Darker gray
    800: '#1F1F1F', // Very dark
    900: '#0F1010', // Almost black
  },

  success: {
    50: '#e8f5e9',
    100: '#c8e6c9',
    200: '#a5d6a7',
    300: '#81c784',
    400: '#66bb6a',
    500: '#43a047', // Main success
    600: '#388e3c',
    700: '#2e7d32',
    800: '#1b5e20',
    900: '#1b5e20',
  },

  warning: {
    50: '#fff3e0',
    100: '#ffe0b2',
    200: '#ffcc80',
    300: '#ffb74d',
    400: '#ffa726',
    500: '#ff9800', // Main warning
    600: '#cc7f16',
    700: '#b36800',
    800: '#994d00',
    900: '#7f3300',
  },

  error: {
    50: '#ffebee',
    100: '#ffcdd2',
    200: '#ef9a9a',
    300: '#e57373',
    400: '#ef5350',
    500: '#d32f2f', // Main error
    600: '#c62828',
    700: '#b71c1c',
    800: '#991b1b',
    900: '#7f1d1d',
  },

  info: {
    50: '#e3f2fd',
    100: '#bbdefb',
    200: '#90caf9',
    300: '#64b5f6',
    400: '#42a5f5',
    500: '#2196f3', // Main info
    600: '#1e88e5',
    700: '#1976d2',
    800: '#1565c0',
    900: '#0d47a1',
  },

  text: {
    primary: '#333333',
    secondary: '#666666',
    disabled: '#8b8589',
    inverse: '#ffffff',
    inverseSecondary: '#e8e8e8',
    light: '#fdfffc',
    muted: '#8b8589',
  },

  background: {
    default: '#ffffff',
    paper: '#fdfffc',
    dark: '#0F1010',
    darkPaper: '#1F1F1F',
  },

  border: {
    light: '#e8e8e8',
    medium: '#cbd6e2',
    dark: '#3f3f3f',
    focus: '#008992',
  },

  hover: {
    background: '#e8e8e8',
  },

  matrix: {
    headerRow: '#b8ebe6',
    headerCell: 'rgba(0, 137, 146, 0.15)',
    inputRow: '#e8f5f3',
    inputCell: '#b8ebe6',
    grayRow: '#fafafa',
    grayCell: '#f5f5f5',
    yellowLightRow: '#fffef0',
    yellowLightCell: '#fff9e6',
    yellowRow: '#fff4cc',
    yellowCell: '#ffefb3',
    orangeRow: '#ffe8d1',
    orangeCell: '#ffd9b3',
    blueRow: '#e6f2ff',
    blueCell: '#cce5ff',
  },
} as const;

// Dark mode color overrides
export const darkModeColors = {
  text: {
    primary: '#fdfffc',
    secondary: '#e8e8e8',
    disabled: '#8b8589',
    inverse: '#0F1010',
    inverseSecondary: '#3f3f3f',
    light: '#fdfffc',
    muted: '#8b8589',
  },
  background: {
    default: '#0F1010',
    paper: '#1F1F1F',
    dark: '#0F1010',
    darkPaper: '#1F1F1F',
  },
  border: {
    light: '#3f3f3f',
    medium: '#666666',
    dark: '#8b8589',
    focus: '#2ec4b6',
  },

  hover: {
    background: '#3f3f3f',
  },

  matrix: {
    headerRow: '#1a3a38',
    headerCell: 'rgba(46, 196, 182, 0.15)',
    inputRow: '#1a2e2c',
    inputCell: '#1f3836',
    grayRow: '#242424',
    grayCell: '#2a2a2a',
    yellowLightRow: '#24241e',
    yellowLightCell: '#2a2a22',
    yellowRow: '#2a2818',
    yellowCell: '#33301c',
    orangeRow: '#2a2418',
    orangeCell: '#33281c',
    blueRow: '#1a2030',
    blueCell: '#1f2838',
  },
} as const;

// Color utility functions
export const getColor = (colorPath: string): string => {
  const keys = colorPath.split('.');
  let value: Record<string, unknown> = colors;

  for (const key of keys) {
    value = value?.[key] as Record<string, unknown>;
    if (value === undefined) {
      return '#000000';
    }
  }

  return value as unknown as string;
};

// Semantic color mappings
export const semanticColors = {
  brand: colors.primary[500],
  accent: colors.secondary[500],
  success: colors.success[500],
  warning: colors.warning[500],
  error: colors.error[500],
  info: colors.info[500],
} as const;
